import React, { useState, useMemo, useCallback } from 'react';
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Lazy / cached Stripe instance
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise && PUBLISHABLE_KEY) {
    stripePromise = loadStripe(PUBLISHABLE_KEY);
  }
  return stripePromise;
};

/**
 * Apple Pay / Google Pay / Link button that lives directly on the cart page
 * or product-detail page. One tap → wallet popup → order created → confirmation.
 *
 * Props:
 *   mode      "cart" | "product"
 *   items     [{ product_id, variant_id, name, color, size, quantity, price, image_url }]
 *   email     optional, prefills payment intent metadata
 *   appliedCredit  optional dollars
 *   deviceId  optional (cart mode uses this to clear the cart on success)
 *   onSuccess optional callback({ order_id })
 *   onError   optional callback(message)
 *   compact   optional, smaller height for product-detail context
 */
const InnerExpress = ({ mode, items, email, appliedCredit, deviceId, onSuccess, onError, compact, onAvailability }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = useCallback(async (event) => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError('');

    try {
      // 1) Create the PaymentIntent server-side for the current cart.
      const intentRes = await fetch(`${API_BASE}/api/checkout/express-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          items,
          email: email || event?.billingDetails?.email || null,
          applied_credit: appliedCredit || 0,
          device_id: deviceId || null,
        }),
      });
      const intentData = await intentRes.json();
      if (!intentRes.ok || !intentData.client_secret) {
        throw new Error(intentData?.detail || 'Failed to start payment');
      }

      // 2) Confirm payment with the wallet's selected method.
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: intentData.client_secret,
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment failed');
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment not completed (status: ${paymentIntent?.status || 'unknown'})`);
      }

      // 3) Pull shipping/billing address from the wallet response.
      const ship = event?.shippingAddress || {};
      const billing = event?.billingDetails || {};
      const shipping_address = {
        name: ship.name || billing.name || '',
        address: [ship.address?.line1, ship.address?.line2].filter(Boolean).join(' ') || billing.address?.line1 || '',
        city: ship.address?.city || billing.address?.city || '',
        state: ship.address?.state || billing.address?.state || '',
        zip_code: ship.address?.postal_code || billing.address?.postal_code || '',
        phone: ship.phone || billing.phone || '',
      };

      // 4) Finalize the order server-side.
      const finalizeRes = await fetch(`${API_BASE}/api/checkout/express-finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_intent_id: paymentIntent.id,
          items,
          email: email || billing.email || ship.email || '',
          shipping_address,
          applied_credit: appliedCredit || 0,
          device_id: deviceId || null,
          mode,
        }),
      });
      const finalizeData = await finalizeRes.json();
      if (!finalizeRes.ok || !finalizeData.success) {
        throw new Error(finalizeData?.detail || 'Failed to finalize order');
      }

      if (onSuccess) onSuccess(finalizeData);
      navigate(`/order-confirmation?order_id=${encodeURIComponent(finalizeData.order_id)}&express=1`);
    } catch (err) {
      console.error('Express checkout error:', err);
      const msg = err?.message || 'Something went wrong';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setBusy(false);
    }
  }, [stripe, elements, mode, items, email, appliedCredit, deviceId, onSuccess, onError, navigate]);

  // Track whether ANY wallet (Apple Pay / Google Pay / Link / etc.) is
  // actually rendered for this device. If Stripe reports zero methods,
  // the element renders nothing and parents may want to show their own
  // fallback "BUY NOW" button.
  const handleReady = useCallback((event) => {
    const methods = (event && event.availablePaymentMethods) || {};
    const hasAny =
      !!methods.applePay ||
      !!methods.googlePay ||
      !!methods.link ||
      !!methods.paypal ||
      !!methods.amazonPay ||
      !!methods.cashAppPay;
    if (onAvailability) {
      try { onAvailability(hasAny, methods); } catch (e) { /* swallow */ }
    }
  }, [onAvailability]);

  return (
    <div data-testid="express-checkout-wrapper" style={{ width: '100%' }}>
      <ExpressCheckoutElement
        onConfirm={handleConfirm}
        onReady={handleReady}
        options={{
          buttonHeight: compact ? 44 : 48,
          buttonTheme: { applePay: 'black', googlePay: 'black', paypal: 'gold' },
          buttonType: { applePay: 'plain', googlePay: 'plain' },
        }}
      />
      {busy && (
        <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '11px', color: '#666' }}>
          PROCESSING...
        </div>
      )}
      {error && (
        <div style={{
          marginTop: '8px', padding: '8px 12px', background: '#fee',
          border: '1px solid #fbb', color: '#900', fontSize: '11px',
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

const ExpressCheckoutButton = (props) => {
  const { items } = props;

  // ALL hooks must be called BEFORE any early return (Rules of Hooks).
  // Compute amount in cents for the Elements provider.
  const amountCents = useMemo(() => {
    if (!items || items.length === 0) return 0;
    let t = 0;
    for (const it of items) {
      const qty = Math.max(1, parseInt(it.quantity || 1, 10));
      t += Math.round(parseFloat(it.price || 0) * 100) * qty;
    }
    const credit = Math.max(0, Math.round(parseFloat(props.appliedCredit || 0) * 100));
    return Math.max(0, t - Math.min(credit, t));
  }, [items, props.appliedCredit]);

  // Don't render if Stripe key missing, no items, or amount below Stripe min ($0.50).
  if (!PUBLISHABLE_KEY) return null;
  if (!items || items.length === 0) return null;
  if (amountCents < 50) return null;

  const options = {
    mode: 'payment',
    amount: amountCents,
    currency: 'usd',
    paymentMethodCreation: 'manual',
    appearance: { theme: 'stripe' },
  };

  return (
    <Elements stripe={getStripe()} options={options}>
      <InnerExpress {...props} />
    </Elements>
  );
};

export default ExpressCheckoutButton;
